import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { staffTimeOffAPI } from 'api/temple'
import { apiStatus } from 'shared/constants/AppEnum'
import { AppState } from 'store/reduxStore'
import { IApiState } from 'store/types'
import { StaffTimeOffPayloadType, StaffTimeOffResponseType, TempleIdType } from 'types/api/temple'
import { selectCurrentTempleId } from './templeSlice'

const staffTimeOffAdapter = createEntityAdapter<StaffTimeOffResponseType>({
    sortComparer: (a, b) => `${b.startDate} ${b.startTime ?? ''}`.localeCompare(`${a.startDate} ${a.startTime ?? ''}`),
})

type InitialStateType = {
    api: IApiState
}

const initialState: InitialStateType = {
    api: {
        status: apiStatus.IDLE,
        error: null,
    },
}

export const staffTimeOffListThunk = createAsyncThunk(
    'staffTimeOff/list',
    async (payload: TempleIdType = {}, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffTimeOffAPI.list(templeId)
            return response.data.map(timeOff => ({ ...timeOff, templeId }))
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffTimeOffRetrieveThunk = createAsyncThunk(
    'staffTimeOff/retrieve',
    async (payload: IdType & TempleIdType, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffTimeOffAPI.get(payload.id, templeId)
            return { ...response.data, templeId }
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffTimeOffApplyThunk = createAsyncThunk(
    'staffTimeOff/apply',
    async (payload: StaffTimeOffPayloadType & TempleIdType, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffTimeOffAPI.apply(payload, templeId)
            return { ...response.data, templeId }
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffTimeOffUpdateThunk = createAsyncThunk(
    'staffTimeOff/update',
    async (payload: IdType & TempleIdType & { data: Partial<StaffTimeOffPayloadType> }, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            const response = await staffTimeOffAPI.update(payload.id, payload.data, templeId)
            return { ...response.data, templeId }
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

export const staffTimeOffDeleteThunk = createAsyncThunk(
    'staffTimeOff/delete',
    async (payload: IdType & TempleIdType, { rejectWithValue, getState }) => {
        const templeId = payload.templeId ?? selectCurrentTempleId(getState() as AppState)
        try {
            await staffTimeOffAPI.delete(payload.id, templeId)
            return payload.id
        } catch (err: any) {
            return rejectWithValue(err.response.data)
        }
    }
)

const staffTimeOffSlice = createSlice({
    name: 'staffTimeOff',
    initialState: staffTimeOffAdapter.getInitialState(initialState),
    reducers: {
        resetState(state) {
            state.api = {
                status: apiStatus.IDLE,
                error: null,
            }
            staffTimeOffAdapter.removeAll(state)
        },
    },
    extraReducers: builder => {
        const pending = (state: any) => {
            state.api = {
                status: apiStatus.LOADING,
                error: null,
            }
        }
        const rejected = (state: any, action: any) => {
            state.api = {
                status: apiStatus.FAILED,
                error: action.payload,
            }
        }

        builder.addCase(staffTimeOffListThunk.pending, pending)
        builder.addCase(staffTimeOffListThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            staffTimeOffAdapter.upsertMany(state, action.payload)
        })
        builder.addCase(staffTimeOffListThunk.rejected, rejected)
        builder.addCase(staffTimeOffRetrieveThunk.pending, pending)
        builder.addCase(staffTimeOffRetrieveThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            staffTimeOffAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffTimeOffRetrieveThunk.rejected, rejected)
        builder.addCase(staffTimeOffApplyThunk.pending, pending)
        builder.addCase(staffTimeOffApplyThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            staffTimeOffAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffTimeOffApplyThunk.rejected, rejected)
        builder.addCase(staffTimeOffUpdateThunk.pending, pending)
        builder.addCase(staffTimeOffUpdateThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            staffTimeOffAdapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffTimeOffUpdateThunk.rejected, rejected)
        builder.addCase(staffTimeOffDeleteThunk.pending, pending)
        builder.addCase(staffTimeOffDeleteThunk.fulfilled, (state, action) => {
            state.api = { status: apiStatus.SUCCEEDED, error: null }
            staffTimeOffAdapter.removeOne(state, action.payload)
        })
        builder.addCase(staffTimeOffDeleteThunk.rejected, rejected)
    },
})

export default staffTimeOffSlice.reducer

export const { resetState } = staffTimeOffSlice.actions

export const {
    selectAll: selectAllStaffTimeOff,
    selectById: selectStaffTimeOffById,
} = staffTimeOffAdapter.getSelectors((state: AppState) => state.staffTimeOff)

export const selectStaffTimeOffStatus = (state: AppState): IApiState => state.staffTimeOff.api

export const selectCurrentTempleStaffTimeOff = createSelector(
    [selectAllStaffTimeOff, selectCurrentTempleId],
    (items, currentTemple) => items.filter(item => item.templeId === currentTemple)
)
